import { Box, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getHelpTopic } from '../../domain/help/helpTopics';
import { getTopicIcon } from './topicIcons';
import HelpIllustration from './illustrations';

interface HelpSection {
    heading: string;
    paragraphs?: string[];
    bullets?: string[];
}

interface HelpTopicViewProps {
    topicId: string;
}

/**
 * Renders a single help topic (title, summary, illustration and sections) from
 * the `help` i18next namespace. Shared by the per-page drawer and the
 * Documentation Center so both stay identical.
 */
const HelpTopicView = ({ topicId }: HelpTopicViewProps) => {
    const { t } = useTranslation('help');
    const topic = getHelpTopic(topicId);
    if (!topic) return null;

    const sections = t(`topics.${topicId}.sections`, { returnObjects: true });
    const sectionList: HelpSection[] = Array.isArray(sections) ? (sections as HelpSection[]) : [];

    return (
        <Box>
            <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                <Box sx={{ color: 'primary.main', display: 'flex' }}>{getTopicIcon(topic.iconKey)}</Box>
                <Typography variant="h5" fontWeight="bold">
                    {t(`topics.${topicId}.title`)}
                </Typography>
            </Box>

            <Typography variant="subtitle1" color="text.secondary" mb={1}>
                {t(`topics.${topicId}.summary`)}
            </Typography>

            <HelpIllustration illustration={topic.illustration} />

            {sectionList.map(section => (
                <Box key={section.heading} mt={3}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                        {section.heading}
                    </Typography>
                    {(section.paragraphs ?? []).map(paragraph => (
                        <Typography
                            key={paragraph}
                            variant="body1"
                            color="text.primary"
                            sx={{ mb: 2 }}
                        >
                            {paragraph}
                        </Typography>
                    ))}
                    {(section.bullets ?? []).length > 0 && (
                        <List dense disablePadding>
                            {(section.bullets ?? []).map(bullet => (
                                <ListItem key={bullet} alignItems="flex-start" sx={{ py: 0.25 }}>
                                    <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                                        <CheckCircleOutline fontSize="small" color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary={bullet} />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            ))}
        </Box>
    );
};

export default HelpTopicView;
